import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useFocusTrap } from '@/hooks/useFocusTrap'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  className?: string
  bodyClassName?: string
  /** Sobrepõe modais padrão (ex.: fluxo de contrato sobre escolha de etapa). */
  stackPriority?: 'normal' | 'high'
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-6xl',
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
  bodyClassName,
  stackPriority = 'normal',
}: ModalProps) {
  const overlayZ = stackPriority === 'high' ? 'z-[70]' : 'z-50'
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, open)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const titleId = title ? 'modal-title' : undefined
  const descId = description ? 'modal-description' : undefined

  const dialog = (
    <AnimatePresence>
      {open && (
        <div className={`fixed inset-0 ${overlayZ} flex items-end md:items-center justify-center p-0 md:p-4`}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'relative w-full z-10',
              'bg-[var(--bg-card)] border border-[var(--glass-border)]',
              'rounded-t-xl md:rounded-xl shadow-2xl',
              'flex flex-col max-h-[90vh] md:max-h-[85vh] overflow-hidden',
              sizeClasses[size],
              className,
            )}
          >
            {(title || description) && (
              <div className="flex shrink-0 items-start justify-between p-5 border-b border-[var(--glass-border)]">
                <div>
                  {title && (
                    <h2
                      id={titleId}
                      className="font-semibold text-[var(--font-color-h2)]"
                      style={{ fontSize: 'var(--font-size-h3)' }}
                    >
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p
                      id={descId}
                      className="mt-1 text-[var(--font-color-muted)]"
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar"
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            <div className={cn('min-h-0 flex-1 overflow-y-auto p-5', bodyClassName)}>{children}</div>

            {footer && (
              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--glass-border)] px-5 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )

  return createPortal(dialog, document.body)
}
